// App.js
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation
} from 'react-router-dom';
import {
  AppBar,
  Box,
  CssBaseline,
  Toolbar,
  Button
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import logo from './assets/logo.png';

import Domains from './pages/Domain';
import DomainForm from './components/DomainForm';
import ProgressReport from './pages/ProgressReport';
import Patient from './pages/Patient';
import PatientHistory from './pages/PatientHistory';
import ReportDetail from './pages/ReportDetail';
import DeleteUser from './pages/DeleteUser';
import ForgotPassword from './components/Auth/ForgotPassword';
import ResetPassword from './components/Auth/ResetPassword';
import OtpVerify from './components/Auth/OtpVerify';
import ProtectedRoute from './components/ProtectedRoute';
import { AuthProvider, useAuth } from './context/AuthContext';
import AuthTabs from './components/Auth/AuthTabs';
import { SnackbarProvider } from './components/Snackbar';

const theme = createTheme({
  palette: {
    primary: { main: '#ea258b' },
  },
});

function AppBarWithLogout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, logout } = useAuth();

  const hideAppBarRoutes = ['/', '/forgot-password', '/reset-password', '/otp-verify'];
  const shouldHideAppBar = hideAppBarRoutes.includes(location.pathname);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (shouldHideAppBar) return null;

  return (
    <AppBar position="fixed" color="primary" sx={{ zIndex: theme.zIndex.drawer + 1 }}>
      <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <img src={logo} alt="Vqure" style={{ height: 40, marginRight: 10 }} />
        </Box>
        {isAuthenticated && (
          <Button color="inherit" onClick={handleLogout}>Logout</Button>
        )}
      </Toolbar>
    </AppBar>
  );
}

function AppRoutes() {
  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBarWithLogout />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          px: 3,
          mt: '64px',
          width: '100%',
        }}
      >
        <Routes>
          <Route path="/patient" element={<ProtectedRoute><Patient /></ProtectedRoute>} />
          <Route path="/domains" element={<ProtectedRoute><Domains /></ProtectedRoute>} />
          <Route path="/domains/:code" element={<ProtectedRoute><DomainForm /></ProtectedRoute>} />
          <Route path="/report" element={<ProtectedRoute><ProgressReport /></ProtectedRoute>} />
          <Route path="/patient/:patientId/report" element={<ProtectedRoute><ReportDetail /></ProtectedRoute>} />
          <Route path="/patient/:patientId/history" element={<ProtectedRoute><PatientHistory /></ProtectedRoute>} />
          <Route path="/" element={<AuthTabs />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/otp-verify" element={<OtpVerify />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/delete-user" element={<DeleteUser />} />
          <Route path="/404" element={<h1>404 - Not Found</h1>} />
          <Route path="*" element={<Navigate to="/404" />} />
        </Routes>
      </Box>
    </Box>
  );
}

export default function App() {
  return (
    <ThemeProvider theme={theme}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <AuthProvider>
          <SnackbarProvider>
            <Router>
              <AppRoutes />
            </Router>
          </SnackbarProvider>
        </AuthProvider>
      </LocalizationProvider>
    </ThemeProvider>
  );
}
